import React from "react";
import {Button} from "@/components/ui/button.tsx";
import { useAuth } from "@/context/AuthContext";
import { projectsInstance } from "@/services/ProjectsService";
import { useReportsStore } from "@/store/store";
import { useWorkspace } from "@/context/WorkspaceContext";

interface Props {
    isUserSubscribed?: boolean;
    canBeUpgraded?: boolean;
    isFreeReport?: boolean;
    reportId?: number;
}

export const SubscriptionBenefits = ( {isUserSubscribed, canBeUpgraded, isFreeReport, reportId}: Props) => {
    const { token } = useAuth();
    const setGenerateDialogRequest = useReportsStore((s) => s.setGenerateDialogRequest);
    const { activeWorkspace } = useWorkspace();
    
    const shouldShowUnavailableContent = isUserSubscribed && isFreeReport && !canBeUpgraded;
    const shouldShowUpgradeContent = isUserSubscribed && isFreeReport && canBeUpgraded;
    
    const handleUpgrade = async () => {
        if (!reportId) return;
        const res = await projectsInstance.upgradeReport(reportId, token, activeWorkspace?.id);
        const upgradedId = res?.report?.report_id;
        // Stay put; the progress dialog is shell-mounted and navigates to the
        // upgraded report itself once it completes. The old bounce through `/`
        // only existed so Home could mount the polling hook (AITS-540).
        setGenerateDialogRequest(upgradedId as number);
    };
    
    return (
        <div className="pb-4 pt-12">
            <div className="w-[90%] max-w-[500px] m-auto mt-10 rounded-lg bg-gradient-to-br from-vb-accent-amber via-vb-accent-violet to-vb-brand p-[2px]">
                <div className="rounded-lg bg-vb-surface p-6">
                    {shouldShowUnavailableContent ? (
                        <>
                            <h1 className='text-xl font-bold text-gray-800'>This content for this month report is unavailable.</h1>
                            <p className='text-md text-gray-800'>
                                This report was created before your subscription was active, so this content isn't&nbsp;available.
                            </p>
                        </>
                    ) : shouldShowUpgradeContent ? (
                        <>
                            <h1 className='text-xl font-bold text-gray-800'>Content unavailable in free report.</h1>
                            <p className='text-md text-gray-800'>
                                Generate full report
                            </p>
                            <div className='flex justify-center items-center text-md mt-6'>
                                <Button onClick={handleUpgrade}>
                                    Generate full report
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <h1 className='text-xl font-bold text-gray-800'>What You Get With a Subscription</h1>
                            <ul className='text-md m-4 list-disc list-outside space-y-2'>
                                <li><b>See all 19 AI Trust Signals</b></li>
                                <li><b>Personalized</b> AI Trust Signals Analysis</li>
                                <li><b>Recommendations</b> for improvement</li>
                                <li>Marcus AI chat - ask your report questions & get <b>detailed action plans</b></li>
                                <li>Marcus AI Executive Summary - a personalized snap-shot of your status, and a <b>90-day action plan</b></li>
                                <li>Monthly Report Data Refresh</li>
                                <li>Monthly Founders Content</li>
                            </ul>
                            <div className='flex justify-center items-center'>
                                <p className='text-sm text-gray-600 text-center'>
                                    Full-report details are available in upgraded report runs.
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
